import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../api/supabase'
import type { PartyTax } from '../lib/partyTaxStatus'

// The company's EORI, read by id (no is_active filter: a deactivated company stored on the job must still show its numbers).
// Re-reads when the browser tab regains focus, so after staff add an EORI in the company page (new tab) the job page shows it without a reload.
export function usePartyTaxIds(customerId: string): PartyTax & { name: string | null } {
  const [state, setState] = useState<PartyTax & { name: string | null }>({ loaded: false, eori: null, name: null })

  const load = useCallback(async () => {
    if (!customerId) return
    const { data, error } = await supabase.from('customers').select('name, eori').eq('id', customerId).maybeSingle()
    if (error || !data) {
      setState({ loaded: false, error: error?.message ?? 'Company not found', eori: null, name: null })
      return
    }
    setState({ loaded: true, error: null, eori: data.eori ?? null, name: data.name ?? null })
  }, [customerId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ loaded: false, eori: null, name: null })
    load()
  }, [load])

  useEffect(() => {
    if (!customerId) return
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    window.addEventListener('focus', load)
    document.addEventListener('visibilitychange', onVisible)
    return () => { window.removeEventListener('focus', load); document.removeEventListener('visibilitychange', onVisible) }
  }, [customerId, load])

  return state
}
